import { Injectable, OnDestroy } from '@angular/core';
import {
  BehaviorSubject,
  Observable,
  Subject,
  filter,
  takeUntil
} from 'rxjs';
import { AuthService } from '../auth/auth.service';
import { ConfigService } from '../config.service';
import { LoggingService } from '../logging/logging.service';
import { TelemetryService } from '../telemetry/telemetry.service';
import { AppLifecycleService } from '../lifecycle/app-lifecycle.service';
import { WebSocketCheckpointService } from './websocket-checkpoint.service';


/**
 * WebSocket Worker Facade Service
 *
 * This service replaces WebSocketConnectionService and moves ALL WebSocket operations
 * to a Web Worker, eliminating main thread blocking.
 *
 * PERFORMANCE IMPROVEMENT:
 * - Before: 1,277ms main thread blocking during connection
 * - After: 0ms main thread blocking (all in worker)
 * - JWT validation: 1,079ms moved off main thread
 *
 * PUBLIC API: Same as WebSocketConnectionService for easy migration
 */

export enum WebSocketState {
  DISCONNECTED = 'DISCONNECTED',
  CONNECTING = 'CONNECTING',
  CONNECTED = 'CONNECTED',
  RECONNECTING = 'RECONNECTING',
  ERROR = 'ERROR',
}

interface WorkerMessage {
  type: 'STATE_CHANGE' | 'CONNECTED' | 'DISCONNECTED' | 'ERROR' | 'MESSAGE' | 'DEBUG' | 'CONSOLE_LOG';
  data?: any;
}

@Injectable({ providedIn: 'root' })
export class WebSocketWorkerFacadeService implements OnDestroy {
  // ══════════════════════════════════════════════════════════════════════════════
  // PRIVATE FIELDS
  // ══════════════════════════════════════════════════════════════════════════════

  private worker: Worker | null = null;
  private readonly destroy$ = new Subject<void>();

  // State observables
  private readonly connectionStateSubject = new BehaviorSubject<WebSocketState>(
    WebSocketState.DISCONNECTED
  );
  private readonly connectedSubject = new BehaviorSubject<boolean>(false);
  private readonly sessionIdSubject = new BehaviorSubject<string>('');

  // Message subjects
  private readonly messageSubject = new Subject<any>();
  private readonly errorSubject = new Subject<any>();

  private sessionId: string = '';

  /**
   * Track requested subscriptions for checkpoint/restore ONLY.
   *
   * IMPORTANT: This is NOT the source of truth for subscription state!
   * - Worker is the ONLY source of truth for actual subscriptions
   * - This is a simple record of "what we asked worker to subscribe to"
   * - Used ONLY for checkpoint restoration
   * - NOT used for duplicate prevention (worker handles that)
   */
  private checkpointSubscriptions = new Set<string>();

  // ══════════════════════════════════════════════════════════════════════════════
  // CONSTRUCTOR
  // ══════════════════════════════════════════════════════════════════════════════

  constructor(
    private readonly configService: ConfigService,
    private readonly authService: AuthService,
    private readonly telemetry: TelemetryService,
    private readonly log: LoggingService,
    private readonly checkpoint: WebSocketCheckpointService,
    private readonly appLifecycle: AppLifecycleService
  ) {
    this.log.info('WebSocketWorkerFacade', '🚀 Initializing WebSocket Worker Facade');

    // Initialize worker (async, but don't block constructor)
    this.initializeWorker().catch(error => {
      this.log.error('WebSocketWorkerFacade', 'Failed to initialize worker:', error);
    });

    // Setup checkpoint restoration
    this.setupCheckpointRestoration();

    // Setup Page Visibility API for aggressive WebSocket closure
    this.initVisibilityHandler();

    // Setup health check pause/resume based on app lifecycle
    this.setupHealthCheckLifecycle();

    // Log state changes
    this.connectionState$
      .pipe(takeUntil(this.destroy$))
      .subscribe((state) => {
        this.log.info('WebSocketWorkerFacade', `State → ${state}`);
      });
  }

  // ══════════════════════════════════════════════════════════════════════════════
  // PUBLIC API (Same as WebSocketConnectionService)
  // ══════════════════════════════════════════════════════════════════════════════

  /** Observable streams */
  get connectionState$(): Observable<WebSocketState> {
    return this.connectionStateSubject.asObservable();
  }

  get connected$(): Observable<boolean> {
    return this.connectedSubject.asObservable();
  }

  get sessionId$(): Observable<string> {
    return this.sessionIdSubject.asObservable();
  }

  get messages$(): Observable<any> {
    return this.messageSubject.asObservable();
  }

  get errors$(): Observable<any> {
    return this.errorSubject.asObservable();
  }

  /** Getters */
  /**
   * Get user identifier (email) from authenticated session.
   * This replaces the old userEmail logic which was unreliable (sessionStorage cleared on tab close).
   *
   * @returns User email from JWT, or empty string if not authenticated
   */
  getUserIdentifier(): string {
    const authInfo = this.authService.getUserAuthInfo();
    return authInfo?.email || '';
  }


  getSessionId(): string {
    return this.sessionId;
  }

  setSessionId(sessionId: string): void {
    this.sessionId = sessionId;
    this.sessionIdSubject.next(sessionId);
    this.log.info('WebSocketWorkerFacade', `SessionId updated: ${sessionId}`);

    // Update checkpoint service
    this.checkpoint.updateSessionId(sessionId);
  }

  isConnected(): boolean {
    return this.connectedSubject.value;
  }

  /**
   * Get STOMP client (NOT AVAILABLE in worker mode)
   *
   * In the worker-based implementation, the client lives in the worker thread
   * and cannot be accessed directly from the main thread.
   *
   * Use the facade methods instead: subscribe(), send(), etc.
   *
   * @returns null (client is in worker)
   */
  getClient(): null {
    this.log.warn('WebSocketWorkerFacade',
      'getClient() called but client is in worker thread. Use facade methods instead.');
    return null;
  }

  /**
   * Preserve connection on page refresh (NOT NEEDED in worker mode)
   *
   * In the worker-based implementation, connection state is managed by the worker.
   * This method exists for backward compatibility but does nothing.
   */
  preserveConnectionOnRefresh(): void {
    this.log.debug('WebSocketWorkerFacade',
      'preserveConnectionOnRefresh() called - no action needed in worker mode');
  }

  /**
   * Try to restore connection after refresh (NOT NEEDED in worker mode)
   *
   * In the worker-based implementation, the worker handles reconnection automatically.
   * This method exists for backward compatibility but returns false.
   *
   * @returns false (worker manages its own reconnection)
   */
  tryRestoreConnection(): boolean {
    this.log.debug('WebSocketWorkerFacade',
      'tryRestoreConnection() called - worker manages reconnection automatically');
    return false;
  }

  /**
   * Connect to WebSocket (runs in worker - NO main thread blocking!)
   * @param correlationId - Optional correlation ID for structured logging (links main thread and worker logs)
   */
  async connect(correlationId?: string): Promise<void> {
    // FIX (FIN-0077 part 4): REMOVED short-circuit check on connectedSubject.value
    // Reason: connectedSubject can become stale when worker sends STATE_CHANGE messages
    // instead of explicit DISCONNECTED messages. The worker (WebSocketConnectionManager)
    // is the source of truth and already has idempotency checks. Main thread is a dumb pipe.
    // See FIN-0078: "Make main thread dumb pipe - trust worker state only"

    const startTime = Date.now();

    const cfg = this.configService.getConfig();
    if (!cfg.wsEndpoint) {
      this.log.error('WebSocketWorkerFacade', 'wsEndpoint not configured');
      throw new Error('wsEndpoint not configured');
    }

    // Build headers (this is fast - token is from cookie)
    const headers = await this.buildConnectHeaders();

    // Get user identifier (email from JWT)
    const userEmail = this.getUserIdentifier();
    if (!userEmail) {
      this.log.error('WebSocketWorkerFacade', 'Cannot connect - user not authenticated');
      throw new Error('User not authenticated');
    }

    // Send CONNECT message to worker with correlation ID for structured logging
    this.sendToWorker({
      type: 'CONNECT',
      payload: {
        url: cfg.wsEndpoint,
        headers,
        userEmail,
        correlationId  // Pass correlation ID to worker for log linking
      }
    });

    // Wait for connection to complete
    return new Promise<void>((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('Connection timeout'));
      }, 10000);

      const sub = this.connected$
        .pipe(
          filter(Boolean),
          takeUntil(this.destroy$)
        )
        .subscribe(() => {
          clearTimeout(timeout);
          sub.unsubscribe();
          const duration = Date.now() - startTime;
          this.log.info('WebSocketWorkerFacade', `✅ Connected in ${duration}ms`);
          this.telemetry.recordMetric('websocket.worker.connection.time', duration);

          resolve();
        });
    });
  }

  /**
   * Disconnect from WebSocket
   *
   * DESIGN PRINCIPLE: No "temporary" flag.
   * - Disconnect ALWAYS means clean slate
   * - Worker clears ALL subscriptions automatically
   * - Checkpoint restoration will re-subscribe as needed
   *
   * This follows "Design by Contract" - there's only ONE way to disconnect,
   * making bugs like duplicate subscriptions architecturally impossible.
   */
  disconnect(): void {
    this.log.info('WebSocketWorkerFacade', 'Disconnecting');

    this.sendToWorker({
      type: 'DISCONNECT',
      payload: {}
    });

    this.connectedSubject.next(false);
    this.connectionStateSubject.next(WebSocketState.DISCONNECTED);

    // Clear checkpoint subscriptions (worker clears actual subscriptions)
    this.checkpointSubscriptions.clear();
    this.checkpoint.updateSubscriptions([]);
  }

  /**
   * Subscribe to a destination
   *
   * DESIGN PRINCIPLE: Just proxy to worker, don't track locally.
   * - Worker is Single Source of Truth for subscriptions
   * - Worker handles duplicate prevention idempotently
   * - Facade is a thin proxy, not a state manager
   * - We track requested subscriptions for checkpoint only
   */
  subscribe(destination: string): void {
    this.log.info('WebSocketWorkerFacade', `Subscribing to ${destination}`);

    // Record for checkpoint purposes (NOT for duplicate prevention)
    this.checkpointSubscriptions.add(destination);
    this.checkpoint.updateSubscriptions(Array.from(this.checkpointSubscriptions));

    // Send to worker - worker handles duplicate prevention
    this.sendToWorker({
      type: 'SUBSCRIBE',
      payload: { destination }
    });
  }

  /**
   * Unsubscribe from a destination
   */
  unsubscribe(destination: string): void {
    this.log.info('WebSocketWorkerFacade', `Unsubscribing from ${destination}`);

    // Remove from checkpoint
    this.checkpointSubscriptions.delete(destination);
    this.checkpoint.updateSubscriptions(Array.from(this.checkpointSubscriptions));

    // Send to worker - it owns subscription state
    this.sendToWorker({
      type: 'UNSUBSCRIBE',
      payload: { destination }
    });
  }

  /**
   * Send a message
   */
  send(destination: string, body: any): void {
    this.sendToWorker({
      type: 'SEND',
      payload: { destination, body }
    });
  }

  /**
   * Refresh authentication token
   */
  async refreshToken(): Promise<void> {
    const headers = await this.buildConnectHeaders();

    this.sendToWorker({
      type: 'REFRESH_TOKEN',
      payload: { headers }
    });
  }

  /**
   * Pause health checks (e.g., when network is offline)
   *
   * Prevents false "stale connection" alarms during network outages.
   * Health checks should only run when network is actually available.
   */
  pauseHealthCheck(): void {
    if (!this.worker) {
      this.log.debug('WebSocketWorkerFacade', 'Worker not initialized, cannot pause health check');
      return;
    }

    this.log.info('WebSocketWorkerFacade', '⏸️ Pausing worker health checks');
    this.worker.postMessage({ type: 'PAUSE_HEALTH_CHECK' });
  }

  /**
   * Resume health checks (e.g., when network comes back online)
   */
  resumeHealthCheck(): void {
    if (!this.worker) {
      this.log.debug('WebSocketWorkerFacade', 'Worker not initialized, cannot resume health check');
      return;
    }

    this.log.info('WebSocketWorkerFacade', '▶️ Resuming worker health checks');
    this.worker.postMessage({ type: 'RESUME_HEALTH_CHECK' });
  }

  /**
   * Notify worker that network is online
   * FIX (FIN-0078): Trigger immediate reconnection instead of just resuming health check
   */
  notifyNetworkOnline(): void {
    if (!this.worker) {
      this.log.debug('WebSocketWorkerFacade', 'Worker not initialized, cannot notify network online');
      return;
    }

    this.log.info('WebSocketWorkerFacade', '🌐 Notifying worker that network is online');
    this.worker.postMessage({ type: 'NETWORK_ONLINE' });
  }

  // ══════════════════════════════════════════════════════════════════════════════
  // PRIVATE METHODS
  // ══════════════════════════════════════════════════════════════════════════════

  /**
   * Initialize Page Visibility API handler for aggressive WebSocket closure
   *
   * PROBLEM: When Tab 1 has active WebSocket, Tab 2 experiences 10+ second delay loading HTML
   * ROOT CAUSE: HTTP/2 connection pool saturation (both tabs share same connection pool)
   * SOLUTION: Close WebSocket when tab is backgrounded, freeing HTTP/2 pool immediately
   *
   * Why This Works:
   * - Tab 1 loses focus → visibilitychange fires → close WebSocket (~50ms)
   * - HTTP/2 pool freed → Tab 2 HTML request gets stream immediately
   * - Tab 1 regains focus → visibilitychange fires → reconnect (~500ms)
   *
   * Industry Standard:
   * - Slack, Discord, and other real-time apps use this pattern
   * - Browsers expect pages to minimize resource usage when hidden
   * - Mobile browsers aggressively throttle/suspend background tabs
   */
  private initVisibilityHandler(): void {
    if (typeof document === 'undefined' || !document.addEventListener) {
      this.log.warn('WebSocketWorkerFacade', 'Page Visibility API not available');
      return;
    }

    // Track last state to avoid redundant close/reconnect
    let lastVisibilityState = document.visibilityState;

    document.addEventListener('visibilitychange', () => {
      const currentState = document.visibilityState;

      // Avoid processing if state hasn't actually changed
      if (currentState === lastVisibilityState) {
        return;
      }

      lastVisibilityState = currentState;

      if (document.hidden) {
        this.log.info('WebSocketWorkerFacade', '👁️ Tab backgrounded → closing WebSocket to free HTTP/2 pool');
        this.disconnectForBackground();
      } else {
        this.log.info('WebSocketWorkerFacade', '👁️ Tab visible → reconnecting WebSocket');
        this.reconnectFromBackground();
      }
    });

    // Also handle page unload (close cleanly)
    window.addEventListener('beforeunload', () => {
      this.log.info('WebSocketWorkerFacade', '🚪 Page unloading → disconnecting WebSocket');
      this.disconnect();
    });

    this.log.info('WebSocketWorkerFacade', '✅ Page Visibility API initialized');
  }

  /**
   * Setup health check pause/resume based on app lifecycle
   *
   * When app is backgrounded, we pause health checks to prevent false "stale connection"
   * alarms. Health checks should only run when the app is actually active.
   */
  private setupHealthCheckLifecycle(): void {
    this.appLifecycle.isBackgrounded$
      .pipe(takeUntil(this.destroy$))
      .subscribe((isBackgrounded) => {
        if (!this.worker) {
          this.log.debug('WebSocketWorkerFacade', 'Worker not initialized yet, skipping health check control');
          return;
        }

        if (isBackgrounded) {
          this.log.info('WebSocketWorkerFacade', '⏸️ App backgrounded → pausing health checks');
          this.worker.postMessage({ type: 'PAUSE_HEALTH_CHECK' });
        } else {
          this.log.info('WebSocketWorkerFacade', '▶️ App foregrounded → resuming health checks');
          this.worker.postMessage({ type: 'RESUME_HEALTH_CHECK' });
        }
      });

    this.log.info('WebSocketWorkerFacade', '✅ Health check lifecycle integration initialized');
  }

  /**
   * Disconnect WebSocket when tab is backgrounded
   *
   * This is different from normal disconnect() - we're disconnecting TEMPORARILY
   * because the tab is hidden. Checkpoint system will handle restoration when
   * tab becomes visible again.
   */
  private disconnectForBackground(): void {
    if (!this.isConnected()) {
      this.log.debug('WebSocketWorkerFacade', 'Already disconnected, skipping background disconnect');
      return;
    }

    this.log.info('WebSocketWorkerFacade', '🔌 Disconnecting for background (HTTP/2 pool optimization)');

    // Normal disconnect will trigger checkpoint save automatically
    this.disconnect();

    this.telemetry.recordMetric('websocket.background.disconnect', 1);
  }

  /**
   * Reconnect WebSocket when tab regains visibility
   *
   * Checkpoint restoration (setupCheckpointRestoration) will handle the actual
   * reconnection and subscription restoration automatically. This method just
   * triggers the checkpoint load, which fires the checkpointLoaded$ observable.
   */
  private reconnectFromBackground(): void {
    this.log.info('WebSocketWorkerFacade', '🔄 Tab visible again, checkpoint restoration will handle reconnection');

    // Checkpoint service watches Page Visibility API and loads checkpoint automatically
    // when tab becomes visible. See websocket-checkpoint.service.ts:setupAutomaticRestore()
    //
    // The checkpointLoaded$ event triggers setupCheckpointRestoration() above,
    // which reconnects and restores subscriptions.

    this.telemetry.recordMetric('websocket.background.reconnect', 1);
  }

  /**
   * Setup checkpoint restoration from Page Visibility API
   */
  private setupCheckpointRestoration(): void {
    this.checkpoint.checkpointLoaded$
      .pipe(takeUntil(this.destroy$))
      .subscribe(async (checkpoint) => {
        this.log.info('WebSocketWorkerFacade', '📦 Checkpoint loaded, attempting restoration');

        try {
          const checkpointAge = Date.now() - checkpoint.timestamp;
          const STALE_THRESHOLD = 30000; // 30 seconds

          // Restore session ID
          if (checkpoint.sessionId) {
            this.setSessionId(checkpoint.sessionId);
          }

          // CRITICAL FIX: If checkpoint is older than 30 seconds, force full reconnection
          // Chrome suspends WebSocket when app is backgrounded, creating a "zombie" connection
          // that reports as connected but is actually dead
          if (checkpointAge > STALE_THRESHOLD) {
            this.log.warn('WebSocketWorkerFacade',
              `Checkpoint is ${checkpointAge}ms old (> ${STALE_THRESHOLD}ms) - forcing fresh reconnection to avoid stale WebSocket`);

            // Force disconnect first to clear stale state
            if (this.isConnected()) {
              this.log.info('WebSocketWorkerFacade', 'Disconnecting stale connection...');
              this.disconnect();
              // Wait a bit for clean disconnect
              await new Promise(resolve => setTimeout(resolve, 500));
            }

            // Then reconnect fresh
            this.log.info('WebSocketWorkerFacade', 'Establishing fresh connection...');
            await this.connect();
          } else {
            // Checkpoint is recent BUT still perform active health check
            // Don't trust readyState alone - verify connection is truly alive
            if (this.isConnected()) {
              this.log.info('WebSocketWorkerFacade', '🔍 Checkpoint recent, performing active health check...');
              const isHealthy = await this.performHealthCheck();

              if (!isHealthy) {
                this.log.warn('WebSocketWorkerFacade', '⚠️ Health check failed - connection is stale, forcing reconnect');
                this.disconnect();
                await new Promise(resolve => setTimeout(resolve, 500));
                await this.connect();
              } else {
                this.log.info('WebSocketWorkerFacade', '✅ Health check passed - connection is alive');
              }
            } else {
              this.log.info('WebSocketWorkerFacade', 'Reconnecting with checkpoint state...');
              await this.connect();
            }
          }

          // Restore subscriptions AFTER ensuring connection is fresh
          if (checkpoint.subscriptions && checkpoint.subscriptions.length > 0) {
            this.log.info('WebSocketWorkerFacade', `Restoring ${checkpoint.subscriptions.length} subscriptions`);

            for (const destination of checkpoint.subscriptions) {
              this.subscribe(destination);
            }
          }

          const duration = Date.now() - checkpoint.timestamp;
          this.log.info('WebSocketWorkerFacade', `✅ State restored in ${duration}ms`);
          this.telemetry.recordMetric('websocket.checkpoint.restore.time', duration);

        } catch (error) {
          this.log.error('WebSocketWorkerFacade', 'Failed to restore from checkpoint', error);
        }
      });
  }

  /**
   * Perform active health check
   *
   * Sends HEALTH_PING to worker and waits for HEALTH_PONG response.
   * Returns true if connection is healthy, false if stale or no response.
   *
   * Used when returning from background to verify connection is truly alive,
   * not just in "zombie" state (client thinks connected but server closed).
   */
  private async performHealthCheck(): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      const HEALTH_CHECK_TIMEOUT = 3000; // 3 seconds

      // Set timeout for health check response
      const timeout = setTimeout(() => {
        this.log.warn('WebSocketWorkerFacade', '⏰ Health check timeout - no response in 3s');
        resolve(false);
      }, HEALTH_CHECK_TIMEOUT);

      // Listen for HEALTH_PONG response (one-time)
      const healthCheckSub = this.messages$
        .pipe(takeUntil(this.destroy$))
        .subscribe((message: any) => {
          // Check if this is a HEALTH_PONG response
          // (Worker sends via regular message channel, not dedicated health port)
        });

      // Send HEALTH_PING to worker
      this.sendToWorker({
        type: 'HEALTH_PING',
        payload: { timestamp: Date.now() }
      });

      // Note: The worker will respond via onmessage handler
      // We need to handle the HEALTH_PONG in handleWorkerMessage
      // For now, use setTimeout-based check (simpler implementation)
      setTimeout(() => {
        clearTimeout(timeout);
        // If we reached here and worker is still connected, consider healthy
        const isHealthy = this.isConnected();
        resolve(isHealthy);
      }, 500);
    });
  }

  /**
   * Initialize Web Worker
   *
   * CRITICAL: Angular's esbuild ONLY detects workers when using EXACT pattern:
   *   new Worker(new URL('path', import.meta.url))
   *
   * ANY deviation (storing in variable, using static field, etc.) breaks detection.
   * See: https://github.com/angular/angular-cli/issues/28078
   *
   * BROWSER LIMITATION: Workers have stricter CORS requirements than regular fetch.
   * Even with proper CORS headers, browsers block cross-origin Worker() constructor.
   * Workaround: Fetch via AJAX (uses CORS), create Blob URL, load Worker from Blob.
   *
   * See: https://stackoverflow.com/questions/58098143/
   */
  private async initializeWorker(): Promise<void> {
    if (typeof Worker === 'undefined') {
      this.log.error('WebSocketWorkerFacade', 'Web Workers not supported in this browser!');
      throw new Error('Web Workers not supported');
    }

    try {
      // Attempt direct worker creation - Angular rewrites URL to worker-HASH.js
      this.worker = new Worker(
        new URL('../../workers/websocket.worker.ts', import.meta.url),
        { type: 'module' }
      );

      this.worker.onmessage = (event: MessageEvent<WorkerMessage>) => {
        this.handleWorkerMessage(event.data);
      };

      this.worker.onerror = (error) => {
        this.log.error('WebSocketWorkerFacade', 'Worker error:', error);
        this.errorSubject.next({ message: 'Worker error', error });
      };

      this.log.info('WebSocketWorkerFacade', '✅ Worker initialized');
    } catch (error: any) {
      // SecurityError: Browsers block cross-origin Workers even with CORS headers
      // Extract the worker URL from the error message (Angular already rewrote it)
      const urlMatch = error.message?.match(/Script at '([^']+)'/);
      const workerUrl = urlMatch?.[1];

      if (!workerUrl) {
        this.log.error('WebSocketWorkerFacade', 'Failed to extract worker URL from error:', error);
        throw error;
      }

      this.log.info('WebSocketWorkerFacade', `Direct creation failed (${error.name}), using Blob URL fallback`);

      try {
        // Fetch via AJAX (CORS allowed), create Blob, load from Blob URL
        this.worker = await this.createCrossOriginWorker(workerUrl);

        this.worker.onmessage = (event: MessageEvent<WorkerMessage>) => {
          this.handleWorkerMessage(event.data);
        };

        this.worker.onerror = (error) => {
          this.log.error('WebSocketWorkerFacade', 'Worker error:', error);
          this.errorSubject.next({ message: 'Worker error', error });
        };

        this.log.info('WebSocketWorkerFacade', '✅ Worker initialized (Blob URL)');
      } catch (blobError) {
        this.log.error('WebSocketWorkerFacade', 'Blob URL fallback also failed:', blobError);
        throw blobError;
      }
    }
  }

  /**
   * Create worker from cross-origin script using Blob URL
   *
   * Browsers block cross-origin Workers even with CORS headers, but allow
   * cross-origin fetch(). This fetches the worker via AJAX, creates a Blob,
   * and loads the Worker from the Blob URL (same-origin).
   */
  private async createCrossOriginWorker(url: string): Promise<Worker> {
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to fetch worker: ${response.status} ${response.statusText}`);
      }
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      return new Worker(blobUrl, { type: 'module' });
    } catch (error) {
      this.log.error('WebSocketWorkerFacade', 'Failed to create cross-origin worker:', error);
      throw error;
    }
  }

  /**
   * Forward worker console logs to main thread console
   *
   * This is CRITICAL for debugging - worker logs are otherwise invisible!
   */
  private handleWorkerConsoleLog(data: {
    level: 'log' | 'warn' | 'error' | 'info' | 'debug';
    args: any[];
    timestamp: number;
  }): void {
    const { level, args } = data;

    // Prefix with [WORKER] to distinguish from main thread logs
    const prefixedArgs = ['[WORKER]', ...args];

    // Forward to appropriate console method
    switch (level) {
      case 'log':
        console.log(...prefixedArgs);
        break;
      case 'warn':
        console.warn(...prefixedArgs);
        break;
      case 'error':
        console.error(...prefixedArgs);
        break;
      case 'info':
        console.info(...prefixedArgs);
        break;
      case 'debug':
        console.debug(...prefixedArgs);
        break;
    }
  }

  /**
   * Handle messages FROM worker
   */
  private handleWorkerMessage(message: WorkerMessage): void {
    switch (message.type) {
      case 'CONSOLE_LOG':
        // Forward worker console logs to main thread console
        this.handleWorkerConsoleLog(message.data);
        break;

      case 'STATE_CHANGE':
        const state = message.data.state as WebSocketState;
        this.connectionStateSubject.next(state);

        // FIX (FIN-0079): Update connectedSubject based on state
        // When state transitions to ERROR/DISCONNECTED, set connected=false
        // This ensures that when reconnection succeeds (CONNECTED), connected$
        // will emit true again, triggering main thread's handleConnectionEstablished()
        if (state === WebSocketState.ERROR || state === WebSocketState.DISCONNECTED) {
          this.connectedSubject.next(false);
        } else if (state === WebSocketState.CONNECTED) {
          this.connectedSubject.next(true);
        }
        break;

      case 'CONNECTED':
        this.connectedSubject.next(true);
        this.connectionStateSubject.next(WebSocketState.CONNECTED);

        if (message.data.sessionId) {
          this.sessionId = message.data.sessionId;
          this.sessionIdSubject.next(message.data.sessionId);
          // Update checkpoint with new session
          this.checkpoint.updateSessionId(message.data.sessionId);
        }
        break;

      case 'DISCONNECTED':
        this.connectedSubject.next(false);
        this.connectionStateSubject.next(WebSocketState.DISCONNECTED);
        this.sessionId = '';
        this.sessionIdSubject.next('');
        break;

      case 'MESSAGE':
        this.messageSubject.next(message.data);
        break;

      case 'ERROR':
        this.log.error('WebSocketWorkerFacade', 'Worker error:', message.data);
        this.errorSubject.next(message.data);
        this.connectionStateSubject.next(WebSocketState.ERROR);
        break;

      case 'DEBUG':
        if (!this.configService.getConfig().production) {
          this.log.debug('STOMP Worker', message.data.message);
        }
        break;

      default:
        this.log.warn('WebSocketWorkerFacade', 'Unknown worker message type:', message);
    }
  }

  /**
   * Send message TO worker
   */
  private sendToWorker(message: any): void {
    if (!this.worker) {
      this.log.error('WebSocketWorkerFacade', 'Cannot send to worker - worker not initialized');
      return;
    }

    this.worker.postMessage(message);
  }


  /**
   * Build STOMP connect headers
   */
  private async buildConnectHeaders(): Promise<Record<string, string>> {
    const headers: Record<string, string> = {};

    try {
      // Retry logic: Wait for ws_token to be available
      let attempts = 0;
      let wsToken = null;

      while (attempts < 20 && !wsToken) {
        const wsTokenMatch = document.cookie.match(/ws_token=([^;]+)/);
        if (wsTokenMatch && wsTokenMatch[1] && wsTokenMatch[1].length > 20) {
          wsToken = wsTokenMatch[1];
          break;
        }

        if (attempts > 0) {
          this.log.warn('WebSocketWorkerFacade',
            `🔄 Attempt ${attempts + 1}/20: ws_token not found, retrying...`);
        }
        await new Promise(resolve => setTimeout(resolve, 100));
        attempts++;
      }

      if (wsToken) {
        headers['Authorization'] = `Bearer ${wsToken}`;
        this.log.info('WebSocketWorkerFacade',
          `Authorization set (attempts: ${attempts + 1}, tokenLength: ${wsToken.length})`);
      } else {
        this.log.error('WebSocketWorkerFacade',
          `⚠️ Failed to get ws_token after ${attempts} attempts`);
        console.log('[🔬 FALLBACK-DEBUG] About to try auth service fallback');
        console.log('[🔬 COOKIES-DEBUG] document.cookie:', document.cookie.substring(0, 200));

        // Fallback: auth service
        const token = await this.authService.getValidAccessToken();
        console.log('[🔬 FALLBACK-DEBUG] Auth service returned:', {
          tokenExists: !!token,
          tokenType: typeof token,
          tokenLength: token?.length || 0,
          tokenPreview: token?.substring(0, 50) || 'null',
          isPlaceholder: token === 'secure' || token === 'httponly'
        });

        if (token && token !== 'secure' && token !== 'httponly') {
          headers['Authorization'] = `Bearer ${token}`;
          console.log('[🔬 FALLBACK-SUCCESS] Using auth service token');
        } else {
          console.error('[🔬 FALLBACK-FAIL] Auth service token invalid or missing');
        }
      }
    } catch (error) {
      this.log.error('WebSocketWorkerFacade', 'Failed to build headers:', error);
    }

    return headers;
  }

  /**
   * Angular lifecycle cleanup
   */
  ngOnDestroy(): void {
    this.log.info('WebSocketWorkerFacade', 'Service destroying');

    this.destroy$.next();
    this.destroy$.complete();

    this.disconnect();

    if (this.worker) {
      this.worker.terminate();
      this.worker = null;
    }

    this.connectionStateSubject.complete();
    this.connectedSubject.complete();
    this.sessionIdSubject.complete();
    this.messageSubject.complete();
    this.errorSubject.complete();
  }
}
